"use client";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { Button } from "@/components/ui/button";
import type { ChangePasswordInput } from "@/types/user";
import { ChangePasswordInputSchema } from "@/types/user";
import { PasswordInput } from "@/components/ui/password-input";
import { authClient } from "@/lib/auth-client";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";

interface PasswordSettingsProps {
  onSuccess?: () => void;
}

export function PasswordSettings({ onSuccess }: PasswordSettingsProps) {
  const { t } = useTranslation();
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const passwordForm = useForm<ChangePasswordInput>({
    resolver: zodResolver(ChangePasswordInputSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
    },
  });

  async function onPasswordSubmit(values: ChangePasswordInput) {
    setIsChangingPassword(true);
    try {
      await authClient.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success(t('passwordSettings.updateSuccessToast'));
      passwordForm.reset();
      onSuccess?.();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('passwordSettings.updateErrorToast');
      toast.error(errorMessage);
    } finally {
      setIsChangingPassword(false);
    }
  }

  return (
    <Form {...passwordForm}>
      <form
        onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
        className="space-y-4 p-1" // Use space-y-4 (16px)
      >
        <FormField
          control={passwordForm.control}
          name="currentPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('passwordSettings.currentPasswordLabel')}</FormLabel>
              <FormControl>
                <PasswordInput
                  placeholder={t('passwordSettings.currentPasswordPlaceholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={passwordForm.control}
          name="newPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('passwordSettings.newPasswordLabel')}</FormLabel>
              <FormControl>
                <PasswordInput
                  placeholder={t('passwordSettings.newPasswordPlaceholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={isChangingPassword || !passwordForm.formState.isDirty}
          // Removed mt-4, rely on parent space-y-4
        >
          {isChangingPassword ? t('passwordSettings.changingPasswordButton') : t('passwordSettings.changePasswordButton')}
        </Button>
      </form>
    </Form>
  );
}
